import React, { useState, useEffect } from "react";
import { claimDailyReward } from "../services/rewardsService";
import useServerClock from "../hooks/useServerClock";
import { auth, db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import DailyRewardModal from "../components/DailyRewardModal";
import Tooltip from "../components/Tooltip";

export default function DailyRewardTab() {
  const serverClockMs = useServerClock();

  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [lastClaimedAt, setLastClaimedAt] = useState(null);
  const [userRank, setUserRank] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [rewardInfo, setRewardInfo] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
    `;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, []);

  useEffect(() => {
    let unsubUser = null;

    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (unsubUser) unsubUser();

      if (!user) {
        setLastClaimedAt(null);
        setUserRank(1);
        setInitialized(true);
        return;
      }

      const ref = doc(db, "Users", user.uid);

      unsubUser = onSnapshot(ref, (snap) => {
        const data = snap.data() || {};

        setLastClaimedAt(data.userDailyRewardClaimedAt ?? null);
        setUserRank(data.userRank || 1);
        setInitialized(true);
      });
    });

    return () => {
      if (unsubUser) unsubUser();
      if (typeof unsubAuth === "function") unsubAuth();
    };
  }, []);

  const toDateAny = (val) => {
    if (!val) return null;
    if (typeof val.toDate === "function") return val.toDate();
    if (typeof val === "number") return new Date(val < 1e12 ? val * 1000 : val);
    return new Date(val);
  };

  useEffect(() => {
    if (!serverClockMs || !initialized) return;

    const now = new Date(serverClockMs);

    if (!lastClaimedAt) {
      setReady(true);
      setTimeRemaining(0);
      return;
    }

    const last = toDateAny(lastClaimedAt);
    if (!last || isNaN(last.getTime())) {
      setReady(true);
      setTimeRemaining(0);
      return;
    }

    const nextClaim = new Date(last.getTime() + 24 * 60 * 60 * 1000);
    const diff = nextClaim - now;

    setReady(diff <= 0);
    setTimeRemaining(Math.max(0, diff));
  }, [serverClockMs, lastClaimedAt, initialized]);

  const formatTime = (ms) => {
    const total = Math.floor(ms / 1000);
    const h = String(Math.floor(total / 3600)).padStart(2, "0");
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const isUnlocked = userRank >= 3;

  const handleClaim = async () => {
    if (!ready || loading || !isUnlocked) return;

    setLoading(true);
    try {
      const res = await claimDailyReward();

      if (!res?.success) {
        throw new Error(res?.message || "Failed to claim reward");
      }

      setRewardInfo(res.item);
      setModalOpen(true);

      if (serverClockMs) {
        const optimisticNext = new Date(serverClockMs + 24 * 60 * 60 * 1000);
        setReady(false);
        setTimeRemaining(optimisticNext - new Date(serverClockMs));
      }
    } catch (err) {
      setRewardInfo({ 
        name: err?.message || "System Rejection", 
        rarity: "common", 
        id: "placeholder",
        description: "No data available."
      });
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const isActive = ready && initialized && isUnlocked;

  const buttonStyle = {
    background: isActive ? "rgba(80, 227, 194, 0.1)" : "transparent",
    border: `1px solid ${isActive ? "#50e3c2" : "#333"}`,
    color: isActive ? "#50e3c2" : "#444",
    padding: "10px 20px",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.75rem",
    cursor: isActive && !loading ? "pointer" : "not-allowed",
    width: "100%",
    transition: "all 0.2s ease",
  };

  return (
    <div style={{ padding: "15px", fontFamily: "'JetBrains Mono', monospace" }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '0.6rem' }}>
        <span style={{ color: isActive ? '#50e3c2' : '#f44336' }}>
          {isActive ? "READY" : "WAITING"}
        </span>
      </div>


      {!isUnlocked ? (
        <Tooltip text="Reach Rank 2 to unlock" delay={200}>
          <button style={buttonStyle} disabled>LOCKED</button>
        </Tooltip>
      ) : (
        <button 
          style={buttonStyle} 
          disabled={!ready || loading} 
          onClick={handleClaim}
        >
          {loading ? "CLAIMING..." : ready ? "CLAIM" : `CLAIM IN: ${formatTime(timeRemaining)}`}
        </button>
      )}


      <DailyRewardModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        item={rewardInfo}
      />
    </div>
  );
}